import { useState } from "react";
import { Calendar, CheckCircle2, Clock, Share2, Tag, Ticket } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { Notice } from "../../parts";
import type { BundleDoc } from "./types";
import { bundlePrice, discountLabel, formatDay, percent, type BundleState, type Spots } from "./bundleManage";

// The bundle page's side column (v1's ManageBundleView sidebar and
// BundleInformation): the bundle, sharing it, the spots it has left and the
// bookings being made, and what it cost.

const card = "rounded-xl border bg-card p-6 shadow-sm";

export type PendingOrder = { id: string; title: string; spots: number };

export function BundleHeader({ bundle, state }: { bundle: BundleDoc; state: BundleState }) {
  const [shared, setShared] = useState<"copied" | "failed" | null>(null);
  const share = async () => {
    const url = window.location.href;
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title: bundle.name, text: bundle.description, url });
        return;
      } catch (error) {
        // Closing the share sheet isn't a failure.
        if (error instanceof DOMException && error.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShared("copied");
    } catch {
      setShared("failed");
    }
  };
  const badge = state.cancelled
    ? { label: "Cancelled", tone: "bg-orange-500" }
    : state.expired
      ? { label: "Expired", tone: "bg-gray-500" }
      : { label: "Active", tone: "bg-green-600" };
  return (
    <div className={`${card} space-y-4`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="mb-2 text-2xl font-bold md:text-3xl">{bundle.name}</h1>
          {bundle.description && <p className="text-sm text-muted-foreground md:text-base">{bundle.description}</p>}
        </div>
        <span className={`shrink-0 rounded-md px-2 py-0.5 text-sm font-medium text-white ${badge.tone}`}>{badge.label}</span>
      </div>
      <Button variant="outline" size="sm" onClick={share}>
        <Share2 className="h-4 w-4" aria-hidden />
        Share bundle
      </Button>
      {shared === "copied" && <Notice tone="success">Link copied to your clipboard.</Notice>}
      {shared === "failed" && <Notice tone="error">We couldn’t copy the link. You can copy it from your browser’s address bar.</Notice>}
    </div>
  );
}

export function SpotsCard({ spots, state, pending }: { spots: Spots; state: BundleState; pending: PendingOrder[] }) {
  const booked = spots.used;
  return (
    <div className={card}>
      <h3 className="mb-2 text-sm font-medium text-muted-foreground">Sessions remaining</h3>
      {state.closed && (
        <p className="mb-3 rounded-md border border-orange-200 bg-orange-50 p-2 text-center text-xs text-orange-800">
          {state.cancelled ? "Bundle cancelled - no new bookings" : "Bundle expired - no new bookings"}
        </p>
      )}
      <div className="mb-4 text-center">
        <div className="mb-1 text-5xl font-bold">{Math.max(0, spots.remaining)}</div>
        <div className="text-sm text-muted-foreground">of {spots.total} total</div>
        {spots.pending > 0 && (
          <div className="mt-1 text-xs text-blue-700">
            ({spots.available} available, {spots.pending} pending)
          </div>
        )}
      </div>
      <div
        className="mb-2 h-2 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={spots.total}
        aria-valuenow={booked}
        aria-label="Sessions booked"
      >
        <div className="h-full bg-foreground transition-all" style={{ width: `${percent(booked, spots.total)}%` }} />
      </div>
      <p className="mb-3 text-center text-xs text-muted-foreground">{booked} sessions booked</p>
      {pending.length > 0 && (
        <div className="border-t pt-3">
          <div className="mb-2 flex items-center gap-2">
            <Clock className="h-4 w-4 text-blue-700" aria-hidden />
            <h4 className="text-sm font-medium">Pending bookings</h4>
          </div>
          <ul className="space-y-2">
            {pending.map((order) => (
              <li key={order.id} className="flex items-center justify-between rounded-md bg-blue-50 p-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium">{order.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {order.spots} spot{order.spots !== 1 ? "s" : ""}
                  </p>
                </div>
                <span className="flex items-center gap-1 text-xs font-medium text-blue-700">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-blue-500" aria-hidden />
                  Processing
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function BundleInformation({
  bundle,
  spots,
  timezone,
  money,
}: {
  bundle: BundleDoc;
  spots: Spots;
  timezone: string;
  money: (pence: number) => string;
}) {
  const price = bundlePrice(bundle);
  const discount = bundle.discount && (bundle.discount.value ?? 0) > 0 ? bundle.discount : null;
  const remaining = Math.max(0, spots.remaining);
  return (
    <>
      <div className={card}>
        <h3 className="mb-4 text-sm font-medium text-muted-foreground">Bundle information</h3>
        <dl className="space-y-3">
          <div>
            <dt className="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" aria-hidden />
              Purchased
            </dt>
            <dd className="pl-6 text-sm font-medium">{formatDay(bundle.createdAt, timezone)}</dd>
          </div>
          <div>
            <dt className="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" aria-hidden />
              Expires
            </dt>
            <dd className="pl-6 text-sm font-medium">{formatDay(bundle.expiredDate, timezone)}</dd>
          </div>
        </dl>
        <div className="mt-4 border-t pt-4">
          <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
            <Ticket className="h-4 w-4" aria-hidden />
            Spots remaining
          </div>
          <div className="flex items-center gap-3">
            <div className="text-2xl font-bold">{remaining}</div>
            <div className="text-sm text-muted-foreground">of {spots.total} total</div>
          </div>
          {spots.used > 0 && (
            <div className="mt-1 text-xs text-muted-foreground">
              {spots.used} spot{spots.used !== 1 ? "s" : ""} used
            </div>
          )}
          <div className="mt-2 h-2 w-full rounded-full bg-muted">
            <div className="h-2 rounded-full bg-green-500 transition-all" style={{ width: `${percent(remaining, spots.total)}%` }} />
          </div>
        </div>
        <div className="mt-6 border-t pt-6">
          <div className="mb-1 text-sm text-muted-foreground">{price.label}</div>
          {discount && price.discount > 0 && (
            <div className="mb-1 flex items-center gap-2">
              <span className="text-sm text-muted-foreground line-through">{money(price.original)}</span>
              <span className="rounded bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">-{money(price.discount)}</span>
            </div>
          )}
          <div className="text-2xl font-bold">{money(price.paid)}</div>
          {price.vatPercent !== undefined && <div className="text-xs text-muted-foreground">Includes {price.vatPercent}% VAT</div>}
        </div>
      </div>
      {discount && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-6 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <span className="rounded-lg bg-green-100 p-2">
              <Tag className="h-4 w-4 text-green-700" aria-hidden />
            </span>
            <h3 className="text-sm font-semibold text-green-900">Discount applied</h3>
          </div>
          <dl className="space-y-2 text-sm">
            {discount.code && discount.code !== "CREDIT" && (
              <div className="flex items-center justify-between">
                <dt className="text-green-700">Code:</dt>
                <dd className="rounded bg-background px-2 py-1 font-mono font-semibold text-green-900">{discount.code}</dd>
              </div>
            )}
            {discount.nature === "CREDIT" && (
              <div className="flex items-center justify-between">
                <dt className="text-green-700">Type:</dt>
                <dd className="font-medium text-green-900">Account credit</dd>
              </div>
            )}
            <div className="flex items-center justify-between">
              <dt className="text-green-700">Discount:</dt>
              <dd className="font-semibold text-green-900">{discountLabel(discount, money)}</dd>
            </div>
          </dl>
          {discount.isFromTeacher && (
            <p className="mt-2 flex items-center gap-1 border-t border-green-200 pt-2 text-xs text-green-700">
              <CheckCircle2 className="h-3 w-3" aria-hidden />
              Provider discount
            </p>
          )}
        </div>
      )}
    </>
  );
}
